import { NextSeo } from 'next-seo';
import { UnitListingDetailsModel } from 'src/api/models';
import {
  getFormattedBeds,
  getBathRoomString,
} from 'src/features/listing-page/utils';

export function HousePageMeta({ home }: HousePageMetaProps): JSX.Element {
  const { address, basicInfo, listingInfo } = home;
  const { streetAddress, city, state, zipcode } = address!;
  const { bannerImageUrl, areaInSquareFeet, unitNumber } = basicInfo!;
  const streetAddressWithUnit = `${streetAddress}${unitNumber ? ` Unit ${unitNumber}` : ''}`;

  const houseBasicInfo = [
    getFormattedBeds(basicInfo!),
    getBathRoomString(basicInfo!),
    `${areaInSquareFeet} sqft`,
  ].join(', ');

  const maxLength = 70;
  const separator = ' | Belong';
  const availableLength = maxLength - separator.length;

  let title = `${streetAddressWithUnit}, ${city}, ${state} ${zipcode} `;

  if (title.length > availableLength) {
    title = `${title.slice(0, availableLength - 3)}...`;
  }

  const metaTitle = `${title}${separator}`;
  const metaDescription = `${houseBasicInfo} - ${streetAddressWithUnit}, ${city}, ${state} - ${zipcode}`;

  // `!` throughout: this component already destructured `address`, `basicInfo`
  // and their fields unconditionally above.
  const cityStateString = `${address!.city!.split(' ').join('-').toLowerCase()}-${address!.state!.toLowerCase()}`;
  const canonical = `https://belonghome.com/homes/${cityStateString}/${basicInfo!.unitId}`;

  return (
    <NextSeo
      title={metaTitle}
      description={metaDescription}
      noindex={listingInfo!.isDemoUnit || !basicInfo!.isPublished} // If it is not published then it's a preview
      canonical={canonical}
      openGraph={{
        title: metaTitle,
        description: metaDescription,
        images: [
          {
            url: bannerImageUrl!,
            width: 1200,
            height: 900,
            alt: 'Home banner',
            type: 'image/jpeg',
          },
        ],
      }}
    />
  );
}

type HousePageMetaProps = {
  home: UnitListingDetailsModel;
};
